import * as Sentry from '@sentry/angular';
import { environment } from '../../../../environment';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private readonly sentryEnvironments = ['prod', 'production', 'staging'];
  private allowedInEnvironment =
    this.sentryEnvironments.includes(environment.envName) ||
    environment.production;

  private allowSentry = !!environment.DSN_URL && this.allowedInEnvironment;

  initSentry(): void {
    if (this.allowSentry) {
      Sentry.init({
        dsn: environment.DSN_URL,
        environment: environment.envName,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: false,
            maskAllInputs: true,
          }),
        ],
        tracesSampleRate: environment.envName === 'prod' ? 0.1 : 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        sendDefaultPii: true,
        beforeSend(event: any, hint?: any) {
          const error = hint?.originalException;

          // Filter out expected authentication errors
          if (error && typeof error === 'object') {
            const url = 'url' in error ? (error as any).url : '';

            // Check for HTTP error responses (HttpErrorResponse object)
            if ('status' in error) {
              const status = (error as any).status;

              // Filter out all 4xx client errors (except 429 Too Many Requests)
              // These are typically user errors, not bugs
              if (status >= 400 && status < 500 && status !== 429) {
                // Don't report client errors to Sentry
                return null;
              }

              // For 5xx errors, transform HttpErrorResponse into clean error
              if ('error' in error && 'url' in error && status >= 500) {
                const httpError = error as any;
                event.message = `HTTP ${httpError.status}: ${httpError.message || httpError.statusText}`;
                event.extra = {
                  url: httpError.url,
                  status: httpError.status,
                  statusText: httpError.statusText,
                  errorDetails: httpError.error,
                };
                // Remove HttpErrorResponse object keys to avoid "Object captured as exception" warning
                event.exception = {
                  values: [
                    {
                      type: 'HttpError',
                      value: event.message,
                    },
                  ],
                };
                return event;
              }
            }

            // Filter out "No user logged in" errors
            if (
              'message' in error &&
              (error as any).message === 'No user logged in'
            ) {
              return null;
            }

            // Prevent raw HttpErrorResponse objects from being sent
            // Check for HttpErrorResponse signature (has headers, ok, type, url properties)
            if (
              'headers' in error &&
              'ok' in error &&
              'type' in error &&
              'url' in error
            ) {
              const httpError = error as any;
              const status = httpError.status || 0;

              // Don't send HttpErrorResponse objects as raw exceptions
              if (status >= 400 && status < 500 && status !== 429) {
                return null; // Filter client errors
              }

              // Transform remaining HTTP errors to clean format
              event.message = `HTTP ${status}: ${httpError.message || httpError.statusText || 'Unknown Error'}`;
              event.extra = {
                url: httpError.url,
                status: status,
                statusText: httpError.statusText,
                errorDetails: httpError.error,
              };
              event.exception = {
                values: [
                  {
                    type: 'HttpError',
                    value: event.message,
                  },
                ],
              };
              return event;
            }
          }

          return event;
        },
      });
    }
  }

  setCurrentUser(user: any) {
    if (this.allowSentry) {
      try {
        Sentry.setUser({
          id: user?.id,
          username: user?.profile?.full_name,
          email: user?.profile?.email,
        });
      } catch (err) {}
    }
  }

  clearCurrentUser() {
    if (this.allowSentry) {
      try {
        Sentry.setUser(null);
      } catch (err) {}
    }
  }
}
